import { FieldErrors, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import Input from '@ramsey-design-system/input';
import FormField from '@ramsey-design-system/form-field';
import { useWizard } from 'react-use-wizard';
import {
  useGAFormTracking,
  useHotjarExitIntentSurvey
} from '../../../../../shared/hooks';
import { ReferralFormData, useReferralContext } from '../../../context';
import { trackStepCompleted } from '../../../../../shared/analytics';
import {
  getFormName,
  useTrackStepError,
  useTrackStepStarted
} from '../../../utils';
import {
  StandardNextButton,
  StandardStepSubtitleText,
  StandardStepTitle,
  StepWrapper
} from '../../../components';
import {
  consolidateFirstAndLastNameErrors,
  fullNameInfoSchema,
  NameStepFields
} from '../../../schemas/FullNameSchema';

const STEP_NAME = 'name_step';
const STEP_LABEL = 'What do we call you?';

function FullNameStep() {
  const formRef = useGAFormTracking(STEP_NAME);
  const cancelExitIntentSurvey = useHotjarExitIntentSurvey();
  const { nextStep, activeStep } = useWizard();
  const { referral, updateReferral } = useReferralContext();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<NameStepFields>({
    defaultValues: {
      ...referral
    },
    resolver: yupResolver(fullNameInfoSchema)
  });
  // eslint-disable-next-line react-hooks/incompatible-library
  const fullName = watch('first_name') + ' ' + watch('last_name');

  const consolidatedError = consolidateFirstAndLastNameErrors(errors);

  useTrackStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);
  const trackError = useTrackStepError(activeStep + 1, STEP_NAME, STEP_LABEL);

  async function onValidSubmit(referral: Partial<ReferralFormData>) {
    cancelExitIntentSurvey();
    updateReferral(referral);
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: ['First Name', 'Last Name'],
      answer_values: [referral.first_name!, referral.last_name!],
      form_name: getFormName()
    });
    await nextStep();
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const fullName = e.target.value;
    const nameParts = fullName
      .split(' ')
      .filter((parts) => parts.trim() !== '');
    const first = nameParts[0] || '';
    const last =
      nameParts.length > 1 ? nameParts[nameParts.length - 1] || '' : '';

    // Update the form values
    setValue('first_name', first);
    setValue('last_name', last);
  }

  function onInvalid(formErrors: FieldErrors<NameStepFields>) {
    trackError(
      'validation',
      consolidateFirstAndLastNameErrors(formErrors),
      'full_name'
    );
  }

  return (
    <StepWrapper>
      <form ref={formRef} onSubmit={handleSubmit(onValidSubmit, onInvalid)}>
        <StandardStepTitle>What do we call you?</StandardStepTitle>

        <StandardStepSubtitleText>
          We'll use this to introduce you to your{'\u00A0'}pro.
        </StandardStepSubtitleText>

        <div className="space-y-2 mb-4">
          <FormField
            id="full_name"
            label="Full Name"
            size="lg"
            invalid={!!errors.first_name || !!errors.last_name}
            errors={consolidatedError ? [consolidatedError] : []}
          >
            {() => (
              <Input
                size="lg"
                invalid={!!errors.first_name || !!errors.last_name}
                id="full_name"
                data-testid="tax_full_name_input"
                autoComplete="name"
                defaultValue={fullName}
                onChange={handleChange}
              />
            )}
          </FormField>

          {/* The below inputs are used to hold state for useForm */}
          <input
            {...register('first_name')}
            className={'hidden'}
            aria-hidden={true}
            tabIndex={-1}
          />
          <input
            {...register('last_name')}
            className={'hidden'}
            aria-hidden={true}
            tabIndex={-1}
          />
        </div>

        <div className="mb-4"></div>

        <StandardNextButton />
      </form>
    </StepWrapper>
  );
}

export { FullNameStep };
